import { useEffect, useMemo, useState } from 'react'

import type { AppointmentWindowRepository } from './appointments-repository'
import {
  daySync,
  parseWindowCoverage,
  syncNotice,
  type SyncNotice,
  type WindowCoverage,
} from './appointment-sync'

/**
 * How current the given clinic days are, from the clinic's published day
 * coverage. Null while unknown: no repository support, no time zone, nothing
 * published yet, or a read failure (the appointments themselves still show).
 */
export function useAppointmentSync(
  repository: AppointmentWindowRepository,
  tenantId: string,
  dates: string[],
  timeZone: string | null,
  nowMs: number
): SyncNotice | null {
  const [coverage, setCoverage] = useState<{
    owner: string
    value: Map<number, WindowCoverage> | null
  } | null>(null)
  useEffect(() => {
    const subscribe = repository.subscribeAppointmentCoverage
    if (!subscribe) return
    let active = true
    const unsubscribe = subscribe(
      tenantId,
      (value) => {
        if (active)
          setCoverage({
            owner: tenantId,
            value: parseWindowCoverage(value, tenantId),
          })
      },
      () => {
        if (active) setCoverage({ owner: tenantId, value: null })
      }
    )
    return () => {
      active = false
      unsubscribe()
    }
  }, [repository, tenantId])
  const datesKey = dates.join(':')
  return useMemo(() => {
    const value = coverage?.owner === tenantId ? coverage.value : null
    if (!value || !timeZone) return null
    const days = datesKey
      .split(':')
      .filter(Boolean)
      .map((date) => daySync(value, date, timeZone, nowMs))
      .filter((day) => day !== null)
    return syncNotice(days, nowMs)
  }, [coverage, datesKey, nowMs, tenantId, timeZone])
}
